import type { FilterState, ReconciliationStatus } from "@/features/transaction-reporting/types";

/** YYYY-MM */
export type ReportMonthKey = string;

/** YYYY-H1 | YYYY-H2 */
export type MasHalfYearKey = `${number}-H1` | `${number}-H2`;

const REPORT_MONTH_KEY_PATTERN = /^(\d{4})-(0[1-9]|1[0-2])$/;
const REPORT_HALF_YEAR_KEY_PATTERN = /^(\d{4})-H([12])$/;

const READY_RECONCILIATION_STATUSES: ReconciliationStatus[] = [
  "Fully Reconciled",
  "Manual Reconciled",
];

function formatDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getMonthRange(monthKey: ReportMonthKey): Pick<FilterState, "start_date" | "end_date"> {
  const [year, month] = monthKey.split("-").map(Number);
  return {
    start_date: formatDate(new Date(year, month - 1, 1)),
    end_date: formatDate(new Date(year, month, 0)),
  };
}

function getHalfYearRange(
  halfYearKey: MasHalfYearKey
): Pick<FilterState, "start_date" | "end_date"> {
  const [yearStr, half] = halfYearKey.split("-");

  if (half === "H1") {
    return { start_date: `${yearStr}-01-01`, end_date: `${yearStr}-06-30` };
  }

  return { start_date: `${yearStr}-07-01`, end_date: `${yearStr}-12-31` };
}

export function parseReportMonthKey(raw: string | null): ReportMonthKey | null {
  if (raw == null) {
    return null;
  }

  const trimmed = raw.trim();
  if (!trimmed || !REPORT_MONTH_KEY_PATTERN.test(trimmed)) {
    return null;
  }

  return trimmed;
}

export function parseReportHalfYearKey(raw: string | null): MasHalfYearKey | null {
  if (raw == null) {
    return null;
  }

  const trimmed = raw.trim();
  if (!trimmed || !REPORT_HALF_YEAR_KEY_PATTERN.test(trimmed)) {
    return null;
  }

  return trimmed as MasHalfYearKey;
}

export function buildMonthReportFilters(month: ReportMonthKey): FilterState {
  return {
    date_field: "value_date",
    ...getMonthRange(month),
    vendor_name: ["DBS"],
    company_record_type: ["client"],
    reconciliation_status: READY_RECONCILIATION_STATUSES,
  };
}

export function buildHalfYearReportFilters(period: MasHalfYearKey): FilterState {
  return {
    date_field: "value_date",
    ...getHalfYearRange(period),
    vendor_name: ["DBS"],
    company_record_type: ["client"],
    reconciliation_status: READY_RECONCILIATION_STATUSES,
  };
}
